import React, { FunctionComponent, useEffect, useState } from "react";
import { MDBCol, MDBRow } from "mdbreact";
import { useFlexPackageProducts } from "../../core/hooks/useFlexPackageProducts";
import { FlexPackage, FlexProduct } from "../../domain/entity/FlexPackage";
import { flexBenefitsPlanText } from "../../core/constants/strings";

interface FlexBenefitsProductDropdownProps {
  pkg: FlexPackage;
  index: number;
  label?: string;
}

function dedupeByProductType(products: FlexProduct[]): FlexProduct[] {
  const seen = new Set<number | undefined>();
  return products.filter((product) => {
    if (seen.has(product.productType)) {
      return false;
    }
    seen.add(product.productType);
    return true;
  });
}

/**
 * Per-package product breakdown (Slice 2, PPI-1391). One dropdown per package,
 * replacing FlexBenefitsPlanRow's plain row now that there's something real to
 * expand into -- see the Slice 1 comment this superseded for why it shipped
 * chevron-less. Independent by product identity (Accident/Critical Illness/
 * Membership), not by an independently-varying status: no per-product status source
 * exists anywhere in the shipped backend (see decision #2 in the PPI-1391 handoff), so
 * these render as plain nested rows (identity only) matching the plan list's own
 * sibling rows, not as badges.
 *
 * Fetches on mount rather than lazily on first expand (the original design) -- QA
 * found a real QA package with a real appPid/id whose product catalog is genuinely
 * empty, and a chevron that expands into nothing is worse than no chevron. Whether
 * there's anything to expand into can only be known after the fetch resolves, so the
 * chevron itself doesn't render until then: no id/appPid to fetch with, a resolved
 * empty list, and a failed fetch all render the same plain, non-interactive row as
 * the still-loading state. This trades the old lazy-fetch savings (a member could
 * hold several packages, most never opened) for correctness; that tradeoff only
 * works because dedupePackagesByIdentity (FlexBenefitsPlanCard.tsx) already collapsed
 * the count down to a handful of genuinely distinct packages before this ever mounts.
 */
const FlexBenefitsProductDropdown: FunctionComponent<FlexBenefitsProductDropdownProps> = ({
  pkg,
  index,
  label = flexBenefitsPlanText,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { products, hasFetched, fetchProducts } = useFlexPackageProducts();
  const domId = pkg.id ?? `${index}`;

  useEffect(() => {
    if (pkg.enrollment?.appPid !== undefined && pkg.id !== undefined) {
      fetchProducts(pkg.enrollment.appPid, pkg.id);
    }
    // Fetch identity is fixed for the lifetime of this instance (one mount per
    // package, per FlexBenefitsPlanGroup's `key`) -- intentionally run once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const distinctProducts = dedupeByProductType(products);
  const canExpand = hasFetched && distinctProducts.length > 0;

  if (!canExpand) {
    return (
      <MDBRow className="mb-3">
        <MDBCol size="auto">
          <p className="mb-0 mr-2" id={`flexbenefits-plan-row-${domId}`}>
            {label}
          </p>
        </MDBCol>
      </MDBRow>
    );
  }

  return (
    <>
      <MDBRow className="mb-3">
        <MDBCol
          size="auto"
          className="d-flex align-items-center"
          onClick={() => setIsOpen(!isOpen)}
          style={{ cursor: "pointer" }}
        >
          <p className="mb-0 mr-2" id={`flexbenefits-plan-row-${domId}`}>
            {label}
          </p>
          <i
            className={`fa fa-angle-${isOpen ? "up" : "down"} silver-text`}
            id={`flexbenefits-plan-row-toggle-${domId}`}
          />
        </MDBCol>
      </MDBRow>
      {isOpen && (
        <div className="mb-3" id={`flexbenefits-products-${domId}`}>
          {distinctProducts.map((product) => (
            <MDBRow key={product.productType}>
              <MDBCol size="auto" className="pl-4">
                <p className="mb-0">{product.name}</p>
              </MDBCol>
            </MDBRow>
          ))}
        </div>
      )}
    </>
  );
};

export default FlexBenefitsProductDropdown;
